/* eslint-disable max-lines */
import React, {ComponentProps, useCallback} from "react";
import {useTranslation} from "react-i18next";
import {useAudio} from "../../../providers/audio";
import {
  Avatar,
  AvatarAction,
  ChallengeMode,
  ChoiceUserInputAction,
  ChoiceUserInputBranchState,
  Scalars,
  Scenario,
  SystemAction,
} from "../../../schema";
import {gql} from "@apollo/client";

import Bubble from "../Bubble";
import Phrase, {PhraseVariant, getTextWithStressMarks} from "../../Phrase";
import Link from "../../../ui/link";

import {
  AddIcon,
  CheckmarkCircleIcon,
  CrossCircleIcon,
  DeleteIcon,
  HelpCircleIcon,
  PlayCircleIcon,
  PlayForwardCircleIcon,
} from "../../../ui/icons";
import Checkbox from "../../../ui/checkbox";
import WithTooltip from "../../../ui/tooltip";
import AvatarIcon from "../../AvatarIcon";
import SystemIcon from "../../SystemIcon";

import cn from "classnames";
import classes from "./ChoiceUserInputBubble.module.css";
import ActionCompetenceBadge from "./ActionCompetenceBadge";

type props = React.ComponentProps<typeof Bubble> & {
  action: ChoiceUserInputAction;
  challengeId: string;
  challengeMode: ChallengeMode;
  avatar: Avatar;
  showCustomScore: boolean;
  showMultipleChoice: boolean;
  scenarios?: Scenario[];
  allowScenarios?: boolean;

  onMultipleChoiceToggle?: () => void;
  onAddBranchClick?: () => void;
  onBranchClick?: (branchId: Scalars["ID"]) => void;
  onQuestionClick?: () => void;
  onReactionAddClick?: (
    branchId: Scalars["ID"],
    type: AvatarAction["__typename"] | SystemAction["__typename"],
  ) => void;
  onReactionClick?: (
    branchId: Scalars["ID"],
    type: AvatarAction["__typename"] | SystemAction["__typename"],
    actionId: Scalars["ID"],
  ) => void;
  onReactionDeleteClick?: (
    branchId: Scalars["ID"],
    type: AvatarAction["__typename"] | SystemAction["__typename"],
    actionId: Scalars["ID"],
  ) => void;

  onDeleteBranchClick?: (branchId: Scalars["ID"]) => void;
};

const branchIconComponent = {
  [ChoiceUserInputBranchState.CORRECT]: <CheckmarkCircleIcon className={cn(classes.icon, classes.correctIcon)} />,
  [ChoiceUserInputBranchState.INCORRECT]: <CrossCircleIcon className={cn(classes.icon, classes.incorrectIcon)} />,
  [ChoiceUserInputBranchState.INTERRUPT]: (
    <PlayForwardCircleIcon className={cn(classes.icon, classes.incorrectIcon)} />
  ),
};

export default function ChoiceUserInputBubble({
  avatar,
  action,
  challengeId,
  challengeMode,
  showCustomScore,
  showMultipleChoice,
  scenarios,
  allowScenarios,
  className,

  onMultipleChoiceToggle,
  onAddBranchClick,
  onBranchClick,
  onReactionAddClick,
  onReactionClick,
  onReactionDeleteClick,
  onDeleteBranchClick,
  onQuestionClick,
  ...props
}: props) {
  const {t} = useTranslation();

  const choices = action.branches;
  const isMultipleChoice = showMultipleChoice && !!action.isMultipleChoice;
  const isCompetenceTest = challengeMode === ChallengeMode.COMPETENCE_TEST;

  const changeIsMultipleChoiceHandler = React.useCallback(() => {
    onMultipleChoiceToggle && onMultipleChoiceToggle();
  }, [onMultipleChoiceToggle]);

  const addBranchHandler: React.MouseEventHandler = React.useCallback(() => {
    onAddBranchClick && onAddBranchClick();
  }, [onAddBranchClick]);

  const branchClickHandler: React.MouseEventHandler = React.useCallback(
    (e) => {
      const branchId = e.currentTarget.getAttribute("data-branch-id");

      onBranchClick && onBranchClick(branchId!);
    },
    [onBranchClick],
  );

  const deleteBranchClickHandler: React.MouseEventHandler = React.useCallback(
    (e) => {
      const branchId = e.currentTarget.getAttribute("data-branch-id");

      onDeleteBranchClick && onDeleteBranchClick(branchId!);
    },
    [onDeleteBranchClick],
  );

  const addAvatarReactionClickHandler: React.MouseEventHandler = React.useCallback(
    (e) => {
      const branchId = e.currentTarget.getAttribute("data-branch-id");

      onReactionAddClick && onReactionAddClick(branchId!, "AvatarAction");
    },
    [onReactionAddClick],
  );

  const editAvatarReactionClickHandler: React.MouseEventHandler = React.useCallback(
    (e) => {
      const actionId = e.currentTarget.getAttribute("data-id");
      const branchId = e.currentTarget.getAttribute("data-branch-id");

      onReactionClick && onReactionClick(branchId!, "AvatarAction", actionId!);
    },
    [onReactionClick],
  );

  const deleteAvatarReactionClickHandler: React.MouseEventHandler = React.useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();

      const actionId = e.currentTarget.getAttribute("data-id");
      const branchId = e.currentTarget.getAttribute("data-branch-id");

      onReactionDeleteClick && onReactionDeleteClick(branchId!, "AvatarAction", actionId!);
    },
    [onReactionDeleteClick],
  );

  const addSystemReactionClickHandler: React.MouseEventHandler = React.useCallback(
    (e) => {
      const branchId = e.currentTarget.getAttribute("data-branch-id");

      onReactionAddClick && onReactionAddClick(branchId!, "SystemAction");
    },
    [onReactionAddClick],
  );

  const editSystemReactionClickHandler: React.MouseEventHandler = React.useCallback(
    (e) => {
      const actionId = e.currentTarget.getAttribute("data-id");
      const branchId = e.currentTarget.getAttribute("data-branch-id");

      onReactionClick && onReactionClick(branchId!, "SystemAction", actionId!);
    },
    [onReactionClick],
  );

  const deleteSystemReactionClickHandler: React.MouseEventHandler = React.useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();

      const actionId = e.currentTarget.getAttribute("data-id");
      const branchId = e.currentTarget.getAttribute("data-branch-id");

      onReactionDeleteClick && onReactionDeleteClick(branchId!, "SystemAction", actionId!);
    },
    [onReactionDeleteClick],
  );

  const {audioManager} = useAudio();

  const onPlayClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      const actionId = e.currentTarget.getAttribute("data-id");

      const systemAudio = choices.find((choice) => choice.systemReaction?.id === actionId)?.systemReaction?.audio;

      const avatarAudio = choices.find((choice) => choice.avatarReaction?.id === actionId)?.avatarReaction?.audio;

      if (!systemAudio && !avatarAudio) {
        console.warn("Cannot play: no mediafile");
        return;
      }

      audioManager
        .getPlayback("editor")!
        .play(systemAudio ?? avatarAudio ?? undefined)
        .catch(console.error);
    },
    [audioManager, choices],
  );

  return (
    <Bubble className={cn(classes.root, className)} {...props}>
      {isCompetenceTest && (
        <div className={classes.bubbleTitle} onClick={onQuestionClick}>
          <Phrase
            text={action.questionText}
            variant={PhraseVariant.DEFAULT}
            placeholder={<Bubble.Placeholder>{t("components.UserInputBubble.placeholder")}</Bubble.Placeholder>}
          />
        </div>
      )}

      {isCompetenceTest && action.competence && (
        <ActionCompetenceBadge competenceName={action.competence.name} />
      )}

      {choices.map((branch, idx) => {
        const icon = branchIconComponent[branch.state];

        return (
          <div key={branch.id} className={classes.branch}>
            <div className={classes.branchIcon}>
              <div data-branch-id={branch.id} onClick={branchClickHandler}>
                {showCustomScore ? <div className={classes.customScore}>{branch.customScore}</div> : icon}
              </div>
            </div>

            <div className={classes.branchContent}>
              <div className={classes.branchFirstRow}>
                <button className={classes.expectedText} data-branch-id={branch.id} onClick={branchClickHandler}>
                  <Phrase
                    text={branch.userInput.referenceText}
                    variant={PhraseVariant.DEFAULT}
                    placeholder={
                      <Bubble.Placeholder>
                        {t("components.ChoiceUserInputActionX.branchPlaceholder")}
                      </Bubble.Placeholder>
                    }
                  />
                </button>
                <Link
                  className={classes.branchDeleteIcon}
                  data-branch-id={branch.id}
                  onClick={deleteBranchClickHandler}
                >
                  <DeleteIcon />
                </Link>
              </div>
              {allowScenarios !== false && branch.targetScenarioId && scenarios && !isMultipleChoice && (
                <div style={{fontSize: "12px"}}>
                  {"→  " + scenarios.find(({id}) => id === branch.targetScenarioId)?.name}
                </div>
              )}
              {branch.state !== ChoiceUserInputBranchState.INTERRUPT && !isCompetenceTest && !isMultipleChoice && (
                <>
                  {branch.avatarReaction ? (
                    <ChoiceUserInputActionReaction
                      data-id={branch.avatarReaction.id}
                      data-branch-id={branch.id}
                      avatarAudio={branch.avatarReaction?.audio ?? undefined}
                      icon={<AvatarIcon className={classes.reactionIcon} avatar={avatar} />}
                      text={getTextWithStressMarks(branch.avatarReaction.text)}
                      onClick={editAvatarReactionClickHandler}
                      onPlayClick={onPlayClick}
                      onDeleteClick={deleteAvatarReactionClickHandler}
                    />
                  ) : (
                    <ChoiceUserInputActionAddReaction
                      data-branch-id={branch.id}
                      placeholder={t("components.ChoiceUserInputActionX.addAvatarReaction")}
                      onClick={addAvatarReactionClickHandler}
                    />
                  )}
                  {branch.systemReaction ? (
                    <ChoiceUserInputActionReaction
                      data-id={branch.systemReaction.id}
                      data-branch-id={branch.id}
                      systemAudio={branch.systemReaction?.audio ?? undefined}
                      icon={<SystemIcon className={classes.reactionIcon} />}
                      text={getTextWithStressMarks(branch.systemReaction.text)}
                      onClick={editSystemReactionClickHandler}
                      onPlayClick={onPlayClick}
                      onDeleteClick={deleteSystemReactionClickHandler}
                    />
                  ) : (
                    <ChoiceUserInputActionAddReaction
                      data-branch-id={branch.id}
                      placeholder={t("components.ChoiceUserInputActionX.addSystemReaction")}
                      onClick={addSystemReactionClickHandler}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}

      <div className={classes.settings}>
        {showMultipleChoice && (
          <Link className={cn(classes.setting, classes.multipleChoice)} onClick={changeIsMultipleChoiceHandler}>
            <Checkbox
              checked={isMultipleChoice}
              className={classes.checkbox}
              variant="userChoice"
              onChange={changeIsMultipleChoiceHandler}
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
            <span>{t("components.ChoiceUserInputActionX.multipleChoice.text")}</span>
            <WithTooltip
              className={classes.help}
              as="span"
              helpText={t("components.ChoiceUserInputActionX.multipleChoice.helpText")}
            >
              <HelpCircleIcon />
            </WithTooltip>
          </Link>
        )}
        <Link className={cn(classes.setting, classes.branchAdd)} tabIndex={0} onClick={addBranchHandler}>
          <AddIcon />
          <span>{t("components.ChoiceUserInputActionX.addBranch")}</span>
        </Link>
      </div>
    </Bubble>
  );
}

function ChoiceUserInputActionAddReaction({
  className,
  placeholder,
  ...props
}: ComponentProps<"button"> & {
  placeholder: string;
}) {
  return (
    <button className={cn(classes.reaction, classes.reactionPlaceholder, className)} {...props}>
      <AddIcon />
      <span>{placeholder}</span>
    </button>
  );
}

function ChoiceUserInputActionReaction({
  icon,
  text,
  systemAudio,
  avatarAudio,
  onPlayClick,
  onDeleteClick,
  ...props
}: ComponentProps<"button"> & {
  systemAudio?: string | undefined;
  avatarAudio?: string | undefined;
  icon: React.ReactNode;
  text: React.ReactNode;
  onPlayClick?: React.MouseEventHandler;
  onDeleteClick?: React.MouseEventHandler;
}) {
  return (
    <button className={classes.reaction} {...props}>
      {icon}
      <span>{text}</span>
      {(systemAudio || avatarAudio) && (
        <Link data-id={(props as any)["data-id"]} className={classes.reactionDeleteIcon} onClick={onPlayClick}>
          <PlayCircleIcon />
        </Link>
      )}
      <Link
        data-id={(props as any)["data-id"]}
        data-branch-id={(props as any)["data-branch-id"]}
        className={classes.reactionDeleteIcon}
        onClick={onDeleteClick}
      >
        <DeleteIcon />
      </Link>
    </button>
  );
}

ChoiceUserInputBubble.fragments = {
  root: gql`
    fragment ChoiceUserInputBubble on ChoiceUserInputAction {
      id
      __typename
      isMultipleChoice
      questionText
      hintText
      branches {
        id
        state
        targetScenarioId
        customScore
        targetScenarioId

        userInput {
          id
          referenceText
          questionText
          hintText
        }

        avatarReaction {
          id
          text
        }

        systemReaction {
          id
          text
        }
      }
      competence {
        id
        __typename
        name
      }
    }
  `,
};
